import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useQueryClient } from 'react-query';
import { Link } from "react-router-dom";
import moment from "moment";
import { useSnackbar } from "notistack";
import SweetAlert from 'react-bootstrap-sweetalert';
import ReactTooltip from 'react-tooltip'
import 'react-datepicker/dist/react-datepicker.css';

import NTMButton from "components/Buttons/NTMButton";
import Image from "components/Image";
import { Column, Row } from "components/Layout";
import HereMap from "components/Map/HereMap";
import EditLabelModal from "components/Modal/EditLabelModal";
import NTMTable from "components/Table/NTMTable";
import AssignDevicePanel from "./AssignDevicePanel";

import useCurrentPosition from "hooks/useCurrentPosition";
import { useGetDevices, useGetInstallations } from "service/RadarpestService";
import { deleteElem, DEVICES, getErrorMessage, post, PROJECTS, put } from "../../service/Client";
import { addIcon_top, addIcon_topOver, deleteIcon, deleteIcon_over, editIcon, editIcon_over, focusIcon, focusIcon_over } from 'img/icons';
import { menuIcons, pinIcons } from 'img/icons';
import { roles } from "../../constants";
import style from "./style.module.scss";
import TrapInstallationModal from "components/Modal/TrapInstallationModal";


export default function RadarpestDashboardPage() {

	const queryClient = useQueryClient();
	const { enqueueSnackbar } = useSnackbar();
	let project = useSelector(store => store.session.project);
	let user = useSelector(store => store.users.data);
	const { devices } = useGetDevices()
	const { installations } = useGetInstallations()
	const position = useCurrentPosition()
	const [alert, setAlert] = useState()
	const [isModalOpen, setIsModalOpen] = useState()
	const [installation, setInstallation] = useState()
	const [installationAction, setInstallationAction] = useState()
	const [center, setCenter] = useState()

	const showError = useCallback(e => {
		enqueueSnackbar(getErrorMessage(e), {variant: "error"});
	}, [enqueueSnackbar])

	const getIcon = useCallback(status => {
		switch (status) {
			// undefined
			case 0: return pinIcons.grey
			// ok
			case 1: return pinIcons.green
			// warning
			case 2: return pinIcons.yellow
			// alarm
			case 3: return pinIcons.red
			// full
			case 4: return pinIcons.violet
			// dead
			case 5: return pinIcons.black
		}
		return pinIcons.grey
	}, [])

	function changeLabel(label) {
		post(`${DEVICES}/${isModalOpen}/label`, {body: label, headers: {"Content-Type": "text/plain"}})
			.then(() => queryClient.invalidateQueries('radarpestDevices'))
			.catch(showError);
		setIsModalOpen(null);
	}

	function disassociate(device) {
		put(`${PROJECTS}/${project.id}/devices`, {
			body: devices.filter(n => n.deviceId !== device.deviceId).map(n => n.deviceId)
		})
			.then(() => queryClient.invalidateQueries('radarpestDevices'))
			.catch(showError);
	}

	const submitInstallationInfo = useCallback((installationAction, installation) => {
        switch (installationAction) {
            case 'create':
                post(`radarpest/installation`, {
					params: { pid: project.id, did: installation.deviceSerial},
					body: installation
				})
                    .then(() => {
						queryClient.invalidateQueries('radarpestDevices')
						queryClient.invalidateQueries('radarpestInstallations')
					})
                    .catch(showError)
                break
            case 'update':
                put(`radarpest/installation/${installation.id}`, { body: installation })
					.then(() => {
						queryClient.invalidateQueries('radarpestDevices')
						queryClient.invalidateQueries('radarpestInstallations')
					})
                    .catch(showError)
                break
            default:
                break
        }
        setInstallationAction(null)
    }, [project.id, queryClient, showError])

    function removeInstallation(installation) {
        setAlert(<SweetAlert
            warning
            showCancel
            cancelBtnText={"Annulla"}
            confirmBtnText="Ho capito, esegui"
            confirmBtnBsStyle="primary"
            title="Confermi?"
            onConfirm={() => {
                put(`radarpest/installation/${installation.id}`, { body: { removalTime: moment().unix() * 1000 }})
					.then(() => {
						queryClient.invalidateQueries('radarpestDevices')
						queryClient.invalidateQueries('radarpestInstallations')
					})
                    .catch(showError)
                    .finally(() => setAlert(null))
            }}
            onCancel={() => setAlert(null)}
            focusCancelBtn
        >
            {`L'installazione ${installation.id} sarà terminata`}
        </SweetAlert>)
    }

	function deleteInstallation(installation) {
        setAlert(<SweetAlert
            warning
            showCancel
            cancelBtnText={"Annulla"}
            confirmBtnText="Ho capito, esegui"
            confirmBtnBsStyle="danger"
            title="Confermi?"
            onConfirm={() => {
                put(`radarpest/installation/${installation.id}`, { body: { archived: true }})
					.then(() => {
						queryClient.invalidateQueries('radarpestDevices')
						queryClient.invalidateQueries('radarpestInstallations')
					})
                    .catch(showError)
                    .finally(() => setAlert(null))
            }}
            onCancel={() => setAlert(null)}
            focusCancelBtn
        >
            {`L'installazione ${installation.id} sarà eliminata`}
        </SweetAlert>)
    }


	function installationPopUp(installationId, deviceId, installationTime, removalTime) {
		return `<div class="bubble">
					<div class="name">id installazione</div><div>${installationId}</div>
					<div class="name">id centralina</div><div>${deviceId}</div>
					<div class="name">Data installazione</div>
					<div>${installationTime ? moment(installationTime).tz("Europe/Rome").format("DD/MM/YYYY HH:mm") : '-'}</div>
					<div class="name">Data rimozione</div>
					<div>${removalTime ? moment(removalTime).tz("Europe/Rome").format("DD/MM/YYYY HH:mm") : '-'}</div>
				</div>`;
	}

	let markers = [];
	if (position && position.latitude && position.longitude) {
		markers.push({
			lat: position.latitude,
			lng: position.longitude,
			data: `<div class="bubble">Posizione corrente</div>`,
			icon: pinIcons.azure
		})
	}
	installations.forEach(k => {
		if (k.lat && k.lng) markers.push({
			lat: k.lat,
			lng: k.lng,
			data: installationPopUp(k.id, k.deviceSerial, k.installationTime, k.removalTime),
			icon: getIcon(k.status)
		});
	});

	let mapCenter = center || ((markers && markers.length > 0) && { lat: markers[0].lat, lng: markers[0].lng })

	let tableHeaderDevice = [
		{
			Header: "ID Centralina",
			accessor: 'deviceId',
			filterable: true,
			Cell: props => <Column>
				<Row style={{justifyContent: "space-around"}}>
					<span style={{paddingRight: "0.5rem"}}>{props.original.deviceId}</span>
				</Row>
				{roles.isSuperUser(user) && <Row>
					<NTMButton primary onClick={() => disassociate(props.original)}>Rimuovi</NTMButton>
				</Row>}
			</Column>
		},
		{
			Header: "Label",
			accessor: 'label',
			filterable: true,
			Cell: props => <Row style={{justifyContent: "space-around"}}>
				<span
					style={{paddingRight: "0.5rem"}}>{props.original.label || props.original.deviceId}</span>
				{roles.isAdmin(user) && <Image
					src={editIcon}
					hover={editIcon_over}
					onClick={() => setIsModalOpen(props.original.deviceId)}
				/>}
			</Row>
		},
		{
			Header: "Id Installazione",
			accessor: 'installationId',
			filterable: true,
			Cell: props => <Row style={{justifyContent: "space-around"}}>
				<span style={{paddingRight: "0.5rem"}}>{props.original?.activeInstallation?.id}</span>
			</Row>
		},
		{
			Header: "Ultima ril",
			accessor: 'receiptDate',
			Cell: props =>
				<div>{props.original.receiptDate ? moment(props.original.receiptDate).tz("Europe/Rome").format("DD/MM/YYYY HH:mm") : '-'}</div>
		},
		{
			Header: "Conteggio insetti 1",
			accessor: 'insectCount1',
			Cell: props => <div>{`${+props.original.insectCount1 || "-"}`}</div>
		},
		{
			Header: "Conteggio insetti 2",
			accessor: 'insectCount2',
			Cell: props => <div>{`${+props.original.insectCount2 || "-"}`}</div>
		},
	]

	let tableHeaderInstallation = [
		{
			Header: "ID",
			accessor: 'id',
			filterable: true,
			Cell: props => <span style={{paddingRight: "0.5rem"}}>{props.original.id}</span>
		},
		{
			Header: "ID centralina",
			accessor: 'deviceSerial',
			filterable: true,
			Cell: props => <span style={{paddingRight: "0.5rem"}}>{props.original.deviceSerial}</span>
		},
		{
			Header: "Installazione",
			accessor: 'installationTime',
			Cell: props =>
				<div>{props.original.installationTime ? moment(props.original.installationTime).tz("Europe/Rome").format("DD/MM/YYYY HH:mm") : '-'}</div>
		},
		{
			Header: "Rimozione",
			accessor: 'removalTime',
			Cell: props =>
				<div>{props.original.removalTime ?
					moment(props.original.removalTime).tz("Europe/Rome").format("DD/MM/YYYY HH:mm") :
					roles.isAdmin(user) &&
						<Row style={{ justifyContent: "space-around" }}>
							<NTMButton primary onClick={() => removeInstallation(props.original)}>Rimuovi</NTMButton>
						</Row>
				}</div>
		},
		{
			Header: "Descrizione",
			accessor: 'description1',
			filterable: true,
			Cell: props => <div>{props.original.description1}</div>
		},
		// {
		// 	Header: "Trappola 2",
		// 	accessor: 'description2',
		// 	filterable: true,
		// 	Cell: props => <div>{props.original.description2}</div>
		// },
		{
			Header: "Ultima ril",
			accessor: 'receiptDate',
			Cell: props =>
				<div>{props.original.receiptDate ? moment(props.original.receiptDate).tz("Europe/Rome").format("DD/MM/YYYY HH:mm") : '-'}</div>
		},
		{
			Header: "Conteggio",
			accessor: 'insectCount1',
			Cell: props => <div>{`${+props.original.insectCount1 || "-"}`}</div>
		},
		{
			Header: "Cont. 2",
			accessor: 'insectCount2',
			Cell: props => <div>{`${+props.original.insectCount2 || "-"}`}</div>
		},
		{
			Header: "",
            accessor: 'controls',
            Cell: props => <Column>
				<ReactTooltip effect='solid' />
				<Row style={{ justifyContent: 'space-around' }}>
					<div data-tip="Centra sulla mappa">
						<Image
							src={focusIcon}
							hover={focusIcon_over}
							onClick={() => props.original.lat && props.original.lng
								&& setCenter({ lat: props.original.lat, lng: props.original.lng })}
						/>
					</div>
					<Link
						to={`/trapinst/${props.original.id}/charts`}
						data-tip="Vai ai grafici"
					>
						<Image
							src={menuIcons.chart}
							hover={menuIcons.chart_over}
						/>
					</Link>
					<Link
						to={`/trapinst/${props.original.id}/data`}
						data-tip="Vai ai dati"
					>
						<Image
							src={menuIcons.data}
							hover={menuIcons.data_over}
						/>
					</Link>
				</Row>
				{roles.isAdmin(user) && <Row style={{ justifyContent: 'space-around' }}>
                    <div data-tip="Modifica">
                        <Image
                            src={editIcon}
                            hover={editIcon_over}
                            onClick={() => {
								setInstallation(props.original)
								setInstallationAction('update')
							}}
                        />
                    </div>
					<div data-tip="Elimina">
                        <Image
                            src={deleteIcon}
                            hover={deleteIcon_over}
                            onClick={() => deleteInstallation(props.original)}
                        />
                    </div>
				</Row>}
			</Column>
        }
	]

	return (
		<div className={"animated fadeIn container"}>
			<Row className={style.box}>
				<HereMap
					center={mapCenter || { lat: 40.967320, lng: 17.115670 }}
					zoom={10}
					theme={"normal.day"}
					markers={markers}
					className={style.map}
				/>
			</Row>

			{roles.isAdmin(user) &&
                <Row style={{ paddingLeft: "1rem" }}>
                    <NTMButton onClick={() => {
						let freeDevices = devices.filter(d => !d.activeInstallation)
						if (freeDevices.length > 0) {
							setInstallation(null)
							setInstallationAction('create')
						} else {
							setAlert(<SweetAlert
								type={'info'}
								title={'Info'}
								onConfirm={() => setAlert(null)}
								onCancel={() => setAlert(null)}
							>
								Nessuna centralina disponibile
							</SweetAlert>)
						}
                    }}>
						<Image
							src={addIcon_top}
							hover={addIcon_topOver}
						/>
						&nbsp;&nbsp;Nuova installazione
					</NTMButton>
                </Row>
            }
			<Row>
				{
					installations.length === 0 &&
						<Row style={{color:"#D8232A", paddingBottom:"1rem", fontWeight:"bold"}}>
							Non è ancora stata associata nessuna installazione a questo progetto
						</Row>
				}
				{
					installations.length > 0 &&
						<NTMTable
							title="Installazioni"
							header={tableHeaderInstallation}
							body={installations}
							defaultPageSize={5}
						/>
				}
			</Row>

			<Row>
				{
					devices.length === 0 &&
						<Row style={{color:"#D8232A", paddingBottom:"1rem", fontWeight:"bold"}}>
							Non è ancora stato associato nessun dispositivo a questo progetto
						</Row>
				}
				{
					devices.length > 0 &&
						<NTMTable
							title="Centraline"
							header={tableHeaderDevice}
							body={devices}
							defaultPageSize={5}
						/>
				}
			</Row>

			<AssignDevicePanel
				devices={devices.map(n => n.deviceId)}
				onSave={() => queryClient.invalidateQueries('radarpestDevices')}
			/>

			<EditLabelModal
				isOpen={!!isModalOpen}
				onClose={() => setIsModalOpen(!isModalOpen)}
				onSubmit={changeLabel}
			/>
			<TrapInstallationModal
				mode={installationAction}
				onClose={() => setInstallationAction(null)}
				onSubmit={submitInstallationInfo}
				installation={installation}
				freeDevices={devices.filter(d => !d.activeInstallation)}
				position={position}
			/>
			{alert}

		</div>
	);

}
